import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, ChevronUp, ShoppingCart, Package, DollarSign, TrendingUp, 
  AlertTriangle, Clock, Eye, Edit2, Check, X, Mail, Send, Calendar, 
  Settings, Play, Pause, RefreshCw, Copy, MapPin, Phone, User, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrdersTab = ({ orders, headers, onOrderUpdate }) => {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [expandedCart, setExpandedCart] = useState(null);
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [cartStats, setCartStats] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' | 'abandoned'
  const [loading, setLoading] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editPaymentStatus, setEditPaymentStatus] = useState('');
  const [editTrackingNumber, setEditTrackingNumber] = useState('');
  
  // Email & Sequence states
  const [sendingEmail, setSendingEmail] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCartForSchedule, setSelectedCartForSchedule] = useState(null);
  const [scheduleDelay, setScheduleDelay] = useState(1);
  const [includeDiscount, setIncludeDiscount] = useState(true);
  
  // Sequence settings states
  const [showSequenceSettings, setShowSequenceSettings] = useState(false);
  const [sequenceSettings, setSequenceSettings] = useState({
    sequence_enabled: true,
    email_1_delay_hours: 1,
    email_2_delay_hours: 24,
    email_3_delay_hours: 72,
    discount_code: 'COMEBACK10',
    discount_value: 10,
    discount_type: 'percentage',
    email_1_include_discount: false,
    email_2_include_discount: true,
    email_3_include_discount: true
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchAbandonedCarts();
    fetchCartStats();
    fetchSequenceSettings();
    calculateOrderStats();
  }, [orders]);

  const fetchAbandonedCarts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/emails/admin/abandoned-carts`, { headers });
      setAbandonedCarts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch abandoned carts:', error);
    }
  };

  const fetchCartStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/emails/admin/abandoned-carts/stats`, { headers });
      setCartStats(response.data);
    } catch (error) {
      console.error('Failed to fetch cart stats:', error);
    }
  };

  const fetchSequenceSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/emails/admin/abandoned-carts/sequence-settings`, { headers });
      setSequenceSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch sequence settings:', error);
    }
  };

  const saveSequenceSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/api/emails/admin/abandoned-carts/sequence-settings`, sequenceSettings, { headers });
      toast.success('Sequence settings saved');
      setShowSequenceSettings(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const sendRecoveryEmail = async (cartId, withDiscount = true) => {
    setSendingEmail(cartId);
    try {
      await axios.post(
        `${API_URL}/api/emails/admin/abandoned-carts/${cartId}/send-email?include_discount=${withDiscount}`,
        {},
        { headers }
      );
      toast.success('Recovery email sent!');
      fetchAbandonedCarts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  const scheduleRecoveryEmail = async () => {
    if (!selectedCartForSchedule) return;
    try {
      await axios.post(
        `${API_URL}/api/emails/admin/abandoned-carts/${selectedCartForSchedule.id}/schedule-email`,
        null,
        { 
          headers,
          params: { delay_hours: scheduleDelay, include_discount: includeDiscount }
        }
      );
      toast.success(`Email scheduled for ${scheduleDelay} hour(s) from now`);
      setShowScheduleModal(false);
      setSelectedCartForSchedule(null);
    } catch (error) {
      toast.error('Failed to schedule email');
    }
  };

  const calculateOrderStats = () => {
    if (!orders || orders.length === 0) {
      setOrderStats(null);
      return;
    }

    // Calculate stats from orders
    const productStats = {};
    let totalRevenue = 0;
    let paidOrders = 0;
    let pendingOrders = 0;

    orders.forEach(order => {
      if (order.payment_status === 'paid') {
        totalRevenue += order.total || 0;
        paidOrders++;
      } else {
        pendingOrders++;
      }

      // Aggregate by product
      (order.items || []).forEach(item => {
        const key = item.name || item.product_id;
        if (!productStats[key]) {
          productStats[key] = {
            name: item.name,
            quantity: 0,
            revenue: 0,
            category: item.category || 'unknown'
          };
        }
        productStats[key].quantity += item.quantity || 1;
        productStats[key].revenue += (item.price || 0) * (item.quantity || 1);
      });
    });

    // Convert to sorted array
    const topProducts = Object.values(productStats)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);

    // Category breakdown
    const categoryStats = {};
    Object.values(productStats).forEach(p => {
      if (!categoryStats[p.category]) {
        categoryStats[p.category] = { quantity: 0, revenue: 0 };
      }
      categoryStats[p.category].quantity += p.quantity;
      categoryStats[p.category].revenue += p.revenue;
    });

    setOrderStats({
      totalRevenue,
      paidOrders,
      pendingOrders,
      topProducts,
      categoryStats,
      totalOrders: orders.length
    });
  };

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const startEditing = (order) => {
    setEditingOrder(order.id);
    setEditStatus(order.status || 'pending');
    setEditPaymentStatus(order.payment_status || 'pending');
    setEditTrackingNumber(order.tracking_number || '');
  };

  const cancelEditing = () => {
    setEditingOrder(null);
    setEditStatus('');
    setEditPaymentStatus('');
    setEditTrackingNumber('');
  };

  const saveOrderChanges = async (orderId) => {
    try {
      setLoading(true);
      const updateData = {};
      
      const currentOrder = orders.find(o => o.id === orderId);
      if (editStatus !== currentOrder?.status) {
        updateData.status = editStatus;
      }
      if (editPaymentStatus !== currentOrder?.payment_status) {
        updateData.payment_status = editPaymentStatus;
      }
      if (editTrackingNumber && editTrackingNumber !== currentOrder?.tracking_number) {
        updateData.tracking_number = editTrackingNumber;
      }

      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        cancelEditing();
        return;
      }

      const params = new URLSearchParams(updateData).toString();
      await axios.put(`${API_URL}/api/orders/${orderId}/status?${params}`, {}, { headers });
      
      toast.success('Order updated successfully');
      cancelEditing();
      
      // Refresh orders if callback provided
      if (onOrderUpdate) {
        onOrderUpdate();
      }
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const now = new Date();
    const hours = Math.floor((now - date) / (1000 * 60 * 60));
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="animate-fade-in">
      <h1 className="text-3xl font-bold text-[#0a0203] mb-8">Orders & Carts</h1>
      
      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'orders' 
              ? 'bg-[#003456] text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="orders-tab-btn"
        >
          Orders ({orders.length})
        </button>
        <button
          onClick={() => setActiveTab('abandoned')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'abandoned' 
              ? 'bg-amber-500 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="abandoned-tab-btn"
        >
          <AlertTriangle className="h-4 w-4" />
          Abandoned Carts ({abandonedCarts.filter(c => !c.recovered).length})
        </button>
      </div>

      {activeTab === 'orders' ? (
        <>
          {/* Orders Table */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 w-10"></th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Order ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Customer</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Items</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Total</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Payment</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <React.Fragment key={order.id}>
                    <tr 
                      className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${expandedOrder === order.id ? 'bg-blue-50' : ''}`}
                      onClick={() => toggleExpand(order.id)}
                    >
                      <td className="px-6 py-4">
                        {expandedOrder === order.id ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm" title={order.id}>{order.id?.slice(0, 8)}...</span>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(order.id);
                              toast && toast.success('Order ID copied!');
                            }}
                            className="text-gray-400 hover:text-[#003456] p-1"
                            title="Copy full Order ID"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-[#0a0203]">{order.customer_name}</p>
                        <p className="text-sm text-gray-500">{order.customer_email}</p>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {order.items?.length || 0} item(s)
                      </td>
                      <td className="px-6 py-4 font-medium" style={{ fontFamily: 'JetBrains Mono' }}>
                        ${order.total?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          order.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-yellow-100 text-yellow-600'
                        }`}>
                          {order.payment_status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          order.status === 'confirmed' ? 'bg-blue-100 text-blue-600' : 
                          order.status === 'shipped' ? 'bg-purple-100 text-purple-600' :
                          order.status === 'delivered' ? 'bg-green-100 text-green-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                    
                    {/* Expanded Order Details */}
                    {expandedOrder === order.id && (
                      <tr className="bg-blue-50">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="bg-white rounded-lg p-4 shadow-inner">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="font-semibold text-gray-700">Order Items</h4>
                                <p className="text-xs text-gray-500 mt-1">
                                  Full Order ID: <span className="font-mono">{order.id}</span>
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(order.id);
                                      toast.success('Order ID copied!');
                                    }}
                                    className="ml-2 text-[#003456] hover:underline text-xs"
                                  >
                                    Copy
                                  </button>
                                </p>
                              </div>
                              {editingOrder !== order.id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditing(order);
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-[#003456] text-white rounded-lg text-sm hover:bg-[#002040] transition-colors"
                                  data-testid="edit-order-btn"
                                >
                                  <Edit2 className="h-4 w-4" />
                                  Edit Order
                                </button>
                              )}
                            </div>
                            
                            {/* Edit Controls */}
                            {editingOrder === order.id && (
                              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <h5 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                  <Edit2 className="h-4 w-4" /> Edit Order Status
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Order Status</label>
                                    <select
                                      value={editStatus}
                                      onChange={(e) => setEditStatus(e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#003456]"
                                      data-testid="edit-order-status"
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="confirmed">Confirmed</option>
                                      <option value="processing">Processing</option>
                                      <option value="shipped">Shipped</option>
                                      <option value="delivered">Delivered</option>
                                      <option value="cancelled">Cancelled</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Payment Status</label>
                                    <select
                                      value={editPaymentStatus}
                                      onChange={(e) => setEditPaymentStatus(e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#003456]"
                                      data-testid="edit-payment-status"
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="paid">Paid</option>
                                      <option value="failed">Failed</option>
                                      <option value="refunded">Refunded</option>
                                      <option value="unpaid">Unpaid</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Tracking Number</label>
                                    <input
                                      type="text"
                                      value={editTrackingNumber}
                                      onChange={(e) => setEditTrackingNumber(e.target.value)}
                                      placeholder="Enter tracking number"
                                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#003456]"
                                      data-testid="edit-tracking-number"
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-2 mt-4">
                                  <button
                                    onClick={() => saveOrderChanges(order.id)}
                                    disabled={loading}
                                    className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
                                    data-testid="save-order-btn"
                                  >
                                    <Check className="h-4 w-4" />
                                    {loading ? 'Saving...' : 'Save Changes'}
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="flex items-center gap-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 transition-colors"
                                    data-testid="cancel-edit-btn"
                                  >
                                    <X className="h-4 w-4" />
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            <table className="w-full">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Product</th>
                                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600">Qty</th>
                                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">MSRP</th>
                                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Final Price</th>
                                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600">Line Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {((order.line_items_calculated && order.line_items_calculated.length > 0) ? order.line_items_calculated : (order.items || [])).map((item, idx) => {
                                  const finalPrice = Number(item.price ?? 0);
                                  const msrp = Number(item.msrp ?? item.price ?? 0);
                                  const qty = Number(item.quantity ?? 1);
                                  const cfg = item.configuration || (order.items && order.items[idx]?.configuration);
                                  const addOns = (cfg && Array.isArray(cfg.add_ons)) ? cfg.add_ons : [];
                                  return (
                                    <React.Fragment key={idx}>
                                      <tr className="border-t border-gray-100">
                                        <td className="px-4 py-3 font-medium">
                                          {item.name}
                                          {item.discount_applied && (
                                            <span className="ml-2 inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800">
                                              {item.discount_applied}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-center">{qty}</td>
                                        <td className="px-4 py-3 text-right text-gray-500" style={{ fontFamily: 'JetBrains Mono' }}>
                                          {msrp ? `$${msrp.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '—'}
                                          {msrp > finalPrice && (
                                            <div className="text-[10px] text-amber-700">−${(msrp - finalPrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-right" style={{ fontFamily: 'JetBrains Mono' }}>
                                          ${finalPrice.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </td>
                                        <td className="px-4 py-3 text-right font-medium" style={{ fontFamily: 'JetBrains Mono' }}>
                                          ${(finalPrice * qty).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </td>
                                      </tr>
                                      {addOns.length > 0 && (
                                        <tr className="bg-gray-50/50">
                                          <td colSpan={5} className="px-6 py-2 text-xs text-gray-600">
                                            <div className="font-semibold mb-1">Add-ons / Configuration:</div>
                                            <ul className="space-y-0.5">
                                              {addOns.map((a, i) => (
                                                <li key={i} className="flex justify-between">
                                                  <span>• {a.name || a.label || JSON.stringify(a)}</span>
                                                  {(a.price != null) && (
                                                    <span style={{ fontFamily: 'JetBrains Mono' }}>+${Number(a.price).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                                  )}
                                                </li>
                                              ))}
                                            </ul>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>

                            {/* Order Financial Breakdown */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <h4 className="font-semibold text-gray-700 mb-3">Order Breakdown</h4>
                              <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                {order.msrp_total != null && (
                                  <div className="flex justify-between"><span className="text-gray-600">MSRP Total</span><span style={{ fontFamily: 'JetBrains Mono' }}>${Number(order.msrp_total).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
                                )}
                                {order.discount_amount > 0 && (
                                  <div className="flex justify-between text-amber-700">
                                    <span>Discount ({order.discount_source || 'unknown'}{order.coupon_code ? `: ${order.coupon_code}` : ''}{order.affiliate_code ? `: ${order.affiliate_code}` : ''}{order.referral_code ? `: ${order.referral_code}` : ''})</span>
                                    <span style={{ fontFamily: 'JetBrains Mono' }}>−${Number(order.discount_amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                  </div>
                                )}
                                {order.subtotal != null && (
                                  <div className="flex justify-between"><span className="text-gray-600">Subtotal (after discount)</span><span style={{ fontFamily: 'JetBrains Mono' }}>${Number(order.subtotal).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
                                )}
                                {order.tax_amount > 0 && (
                                  <div className="flex justify-between"><span className="text-gray-600">{order.tax_label || 'Tax'} ({order.tax_percent}%{order.tax_region ? ` · ${order.tax_region}` : ''})</span><span style={{ fontFamily: 'JetBrains Mono' }}>+${Number(order.tax_amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
                                )}
                                {order.shipping_cost != null && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Shipping{order.shipping_method ? ` (${order.shipping_method})` : ''}</span>
                                    <span style={{ fontFamily: 'JetBrains Mono' }}>{Number(order.shipping_cost) === 0 ? 'FREE' : `$${Number(order.shipping_cost).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}</span>
                                  </div>
                                )}
                                {order.credit_card_surcharge > 0 && (
                                  <div className="flex justify-between"><span className="text-gray-600">Card Processing Fee ({order.credit_card_surcharge_percent || 2.9}%)</span><span style={{ fontFamily: 'JetBrains Mono' }}>+${Number(order.credit_card_surcharge).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
                                )}
                                {order.store_credit_amount > 0 && (
                                  <div className="flex justify-between text-blue-700"><span>Store Credit ({order.store_credit_code})</span><span style={{ fontFamily: 'JetBrains Mono' }}>−${Number(order.store_credit_amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span></div>
                                )}
                                <div className="flex justify-between font-bold text-[#003456] sm:col-span-2 pt-2 mt-1 border-t border-gray-300">
                                  <span>Total ({order.currency || 'CAD'})</span>
                                  <span style={{ fontFamily: 'JetBrains Mono' }}>${Number(order.total || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                              </div>
                            </div>

                            {/* Customer + Payment + Tracking codes */}
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div className="bg-white border rounded-lg p-3">
                                <h5 className="font-semibold text-gray-700 mb-2">Customer</h5>
                                <div className="space-y-1 text-gray-600">
                                  <div><span className="text-gray-400">Name:</span> {order.customer_name || '—'}</div>
                                  <div><span className="text-gray-400">Email:</span> {order.customer_email || '—'}</div>
                                  <div><span className="text-gray-400">Phone:</span> {order.customer_phone || order.phone || '—'}</div>
                                  <div><span className="text-gray-400">Currency:</span> {order.currency || 'CAD'}</div>
                                  <div><span className="text-gray-400">Payment method:</span> {order.payment_method || '—'}</div>
                                  <div><span className="text-gray-400">Postal code:</span> {order.postal_code || '—'}</div>
                                </div>
                              </div>
                              <div className="bg-white border rounded-lg p-3">
                                <h5 className="font-semibold text-gray-700 mb-2">Tracking & Codes</h5>
                                <div className="space-y-1 text-gray-600">
                                  <div><span className="text-gray-400">Discount source:</span> <span className="font-medium">{order.discount_source || 'none'}</span></div>
                                  <div><span className="text-gray-400">Coupon code:</span> {order.coupon_code || '—'}</div>
                                  <div><span className="text-gray-400">Affiliate code:</span> {order.affiliate_code || '—'}</div>
                                  <div><span className="text-gray-400">Campaign code:</span> {order.campaign_code || '—'}</div>
                                  <div><span className="text-gray-400">Referral code:</span> {order.referral_code || '—'}</div>
                                  <div><span className="text-gray-400">Stripe session:</span> <span className="font-mono text-xs">{order.stripe_session_id ? `${order.stripe_session_id.slice(0, 24)}…` : '—'}</span>
                                    {order.stripe_session_id && (
                                      <a
                                        href={
                                          order.stripe_payment_intent_id
                                            ? `https://dashboard.stripe.com/payments/${order.stripe_payment_intent_id}`
                                            : `https://dashboard.stripe.com/payments?query=${encodeURIComponent(order.stripe_session_id)}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-2 inline-flex items-center gap-1 text-[#003456] hover:underline text-xs font-medium"
                                        data-testid="open-stripe-session"
                                      >
                                        Open in Stripe ↗
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Shipping Info */}
                            {(order.shipping_address_structured || order.shipping_address) && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className="font-semibold text-gray-700 mb-2">Shipping Address</h4>
                                {order.shipping_address_structured ? (
                                  <div className="text-gray-700 leading-relaxed">
                                    {order.shipping_address_structured.name && (
                                      <div className="font-medium">{order.shipping_address_structured.name}</div>
                                    )}
                                    {order.shipping_address_structured.line1 && <div>{order.shipping_address_structured.line1}</div>}
                                    {order.shipping_address_structured.line2 && <div>{order.shipping_address_structured.line2}</div>}
                                    <div>
                                      {[order.shipping_address_structured.city,
                                        order.shipping_address_structured.state,
                                        order.shipping_address_structured.postal_code].filter(Boolean).join(', ')}
                                    </div>
                                    {order.shipping_address_structured.country && (
                                      <div>{order.shipping_address_structured.country}</div>
                                    )}
                                    {(() => {
                                      const s = order.shipping_address_structured;
                                      const q = encodeURIComponent([s.line1, s.line2, s.city, s.state, s.postal_code, s.country].filter(Boolean).join(', '));
                                      return q ? (
                                        <a href={`https://www.google.com/maps/search/?api=1&query=${q}`} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-[#003456] hover:underline text-xs font-medium">
                                          Open in Google Maps ↗
                                        </a>
                                      ) : null;
                                    })()}
                                  </div>
                                ) : (
                                  <p className="text-gray-600 whitespace-pre-line">{order.shipping_address}</p>
                                )}
                                {order.tracking_number && (
                                  <p className="mt-2 text-sm"><span className="text-gray-400">Tracking #:</span> <span className="font-mono">{order.tracking_number}</span></p>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && (
              <p className="text-center py-8 text-gray-500">No orders yet</p>
            )}
          </div>

          {/* Order Analytics Dashboard */}
          {orderStats && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-[#0a0203]">Order Analytics</h2>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#0a0203]" style={{ fontFamily: 'JetBrains Mono' }}>
                        ${orderStats.totalRevenue?.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500">Total Revenue</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ShoppingCart className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#0a0203]">{orderStats.totalOrders}</p>
                      <p className="text-sm text-gray-500">Total Orders</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{orderStats.paidOrders}</p>
                      <p className="text-sm text-gray-500">Paid Orders</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Clock className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-yellow-600">{orderStats.pendingOrders}</p>
                      <p className="text-sm text-gray-500">Pending Orders</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Products */}
              {orderStats.topProducts && orderStats.topProducts.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0a0203] mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-[#003456]" />
                    Top Selling Products
                  </h3>
                  <div className="space-y-3">
                    {orderStats.topProducts.map((product, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-amber-500 text-white' :
                            idx === 1 ? 'bg-gray-400 text-white' :
                            idx === 2 ? 'bg-amber-700 text-white' :
                            'bg-gray-200 text-gray-600'
                          }`}>
                            {idx + 1}
                          </span>
                          <span className="font-medium">{product.name}</span>
                        </div>
                        <div className="flex items-center gap-6">
                          <span className="text-gray-600">{product.quantity} units</span>
                          <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                            ${product.revenue?.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Category Breakdown */}
              {orderStats.categoryStats && Object.keys(orderStats.categoryStats).length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-[#0a0203] mb-4">Sales by Category</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(orderStats.categoryStats).map(([category, stats]) => (
                      <div key={category} className="p-4 bg-gray-50 rounded-lg">
                        <p className="font-medium capitalize">{category.replace('-', ' ')}</p>
                        <p className="text-2xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>
                          ${stats.revenue?.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500">{stats.quantity} units sold</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        /* Abandoned Carts Tab */
        <div className="space-y-6">
          {/* Stats Cards */}
          {cartStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <ShoppingCart className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#0a0203]">{cartStats.pending}</p>
                    <p className="text-sm text-gray-500">Abandoned</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{cartStats.recovered}</p>
                    <p className="text-sm text-gray-500">Recovered</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{cartStats.recovery_rate}%</p>
                    <p className="text-sm text-gray-500">Recovery Rate</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600" style={{ fontFamily: 'JetBrains Mono' }}>
                      ${cartStats.potential_revenue?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">Potential Revenue</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sequence Settings Button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={fetchAbandonedCarts}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" /> Refresh
              </Button>
            </div>
            <Button
              onClick={() => setShowSequenceSettings(true)}
              className="bg-[#003456] hover:bg-[#002040] gap-2"
              data-testid="sequence-settings-btn"
            >
              <Settings className="h-4 w-4" /> Email Sequence Settings
            </Button>
          </div>

          {/* Abandoned Carts List */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold text-[#0a0203]">Abandoned Carts</h3>
            </div>
            <div className="divide-y">
              {abandonedCarts.filter(cart => !cart.recovered).map(cart => (
                <div key={cart.id} className="hover:bg-gray-50 transition-colors">
                  {/* Cart Row - Clickable to expand */}
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedCart(expandedCart === cart.id ? null : cart.id)}
                    data-testid={`cart-row-${cart.id?.slice(0, 8)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button className="text-gray-400">
                          {expandedCart === cart.id ? (
                            <ChevronUp className="h-5 w-5" />
                          ) : (
                            <ChevronDown className="h-5 w-5" />
                          )}
                        </button>
                        <div>
                          <p className="font-medium text-[#0a0203]">{cart.customer_name || 'Anonymous'}</p>
                          <p className="text-sm text-gray-500">{cart.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="font-bold text-lg" style={{ fontFamily: 'JetBrains Mono' }}>
                            ${cart.total?.toLocaleString() || 0}
                          </p>
                          <p className="text-xs text-gray-500">{cart.items?.length || 0} items</p>
                        </div>
                        <div className="text-right">
                          <span className="text-amber-600 font-medium text-sm">
                            {getTimeSince(cart.created_at)}
                          </span>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          cart.emails_sent >= 3 ? 'bg-purple-100 text-purple-600' :
                          cart.emails_sent >= 1 ? 'bg-blue-100 text-blue-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {cart.emails_sent || 0} email(s)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Cart Details */}
                  {expandedCart === cart.id && (
                    <div className="bg-amber-50 border-t px-4 py-4" data-testid={`cart-details-${cart.id?.slice(0, 8)}`}>
                      <div className="grid md:grid-cols-3 gap-6">
                        {/* Customer Information */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                            <User className="h-4 w-4" /> Customer Information
                          </h4>
                          <div className="bg-white rounded-lg p-3 space-y-2 text-sm">
                            <p><span className="text-gray-500">Name:</span> {cart.customer_name || 'Anonymous'}</p>
                            <p className="flex items-center gap-1">
                              <Mail className="h-3 w-3 text-gray-400" />
                              <span className="text-gray-500">Email:</span> {cart.email}
                            </p>
                            {cart.phone && (
                              <p className="flex items-center gap-1">
                                <Phone className="h-3 w-3 text-gray-400" />
                                <span className="text-gray-500">Phone:</span> {cart.phone}
                              </p>
                            )}
                            <div className="pt-2 border-t">
                              <p className="text-gray-500 mb-1">Cart Age:</p>
                              <p className="font-medium text-amber-600">{getTimeSince(cart.created_at)}</p>
                            </div>
                            <div className="pt-2 border-t">
                              <p className="text-gray-500 mb-1">Recovery Attempts:</p>
                              <p className="font-medium">{cart.emails_sent || 0} emails sent</p>
                              {cart.last_email_sent && (
                                <p className="text-xs text-gray-400">
                                  Last: {new Date(cart.last_email_sent).toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Cart Items */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                            <ShoppingCart className="h-4 w-4" /> Cart Items
                          </h4>
                          <div className="bg-white rounded-lg p-3 space-y-2 max-h-64 overflow-y-auto">
                            {(cart.items || []).map((item, idx) => (
                              <div key={idx} className="flex items-center gap-3 py-2 border-b last:border-0">
                                {item.image_url && (
                                  <img 
                                    src={item.image_url} 
                                    alt={item.name} 
                                    className="w-12 h-12 rounded-lg object-cover"
                                  />
                                )}
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{item.name}</p>
                                  <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                                </div>
                                <p className="font-medium" style={{ fontFamily: 'JetBrains Mono' }}>
                                  ${(item.price * item.quantity).toLocaleString()}
                                </p>
                              </div>
                            ))}
                            <div className="pt-2 border-t flex justify-between font-bold">
                              <span>Total</span>
                              <span style={{ fontFamily: 'JetBrains Mono' }}>${cart.total?.toLocaleString() || 0}</span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                            <Mail className="h-4 w-4" /> Recovery Actions
                          </h4>
                          <div className="bg-white rounded-lg p-3 space-y-3">
                            {/* Send Email Now */}
                            <Button
                              className="w-full bg-amber-500 hover:bg-amber-600 gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                sendRecoveryEmail(cart.id, true);
                              }}
                              disabled={sendingEmail === cart.id}
                              data-testid={`send-email-btn-${cart.id?.slice(0, 8)}`}
                            >
                              {sendingEmail === cart.id ? (
                                <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>
                              ) : (
                                <><Send className="h-4 w-4" /> Send Recovery Email</>
                              )}
                            </Button>

                            {/* Send Without Discount */}
                            <Button
                              variant="outline"
                              className="w-full gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                sendRecoveryEmail(cart.id, false);
                              }}
                              disabled={sendingEmail === cart.id}
                            >
                              <Mail className="h-4 w-4" /> Send Without Discount
                            </Button>

                            {/* Schedule Email */}
                            <Button
                              variant="outline"
                              className="w-full gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCartForSchedule(cart);
                                setShowScheduleModal(true);
                              }}
                              data-testid={`schedule-email-btn-${cart.id?.slice(0, 8)}`}
                            >
                              <Calendar className="h-4 w-4" /> Schedule Email
                            </Button>

                            {/* Copy Email */}
                            <Button
                              variant="outline"
                              className="w-full gap-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(cart.email);
                                toast.success('Email copied!');
                              }}
                            >
                              <Copy className="h-4 w-4" /> Copy Email Address
                            </Button>

                            {/* Discount Code Info */}
                            <div className="p-3 bg-blue-50 rounded-lg text-sm">
                              <p className="text-blue-700 font-medium">Active Discount Code:</p>
                              <p className="font-mono text-blue-900">{sequenceSettings.discount_code}</p>
                              <p className="text-blue-600 text-xs mt-1">
                                {sequenceSettings.discount_type === 'percentage' 
                                  ? `${sequenceSettings.discount_value}% off`
                                  : `$${sequenceSettings.discount_value} off`}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {abandonedCarts.filter(c => !c.recovered).length === 0 && (
              <p className="text-center py-8 text-gray-500">No abandoned carts</p>
            )}
          </div>

          {/* Recovered Carts Section */}
          {abandonedCarts.filter(c => c.recovered).length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b bg-green-50">
                <h3 className="font-semibold text-green-700 flex items-center gap-2">
                  <Check className="h-5 w-5" /> Recovered Carts ({abandonedCarts.filter(c => c.recovered).length})
                </h3>
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {abandonedCarts.filter(c => c.recovered).slice(0, 10).map(cart => (
                  <div key={cart.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{cart.customer_name || 'Anonymous'}</p>
                      <p className="text-sm text-gray-500">{cart.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600" style={{ fontFamily: 'JetBrains Mono' }}>
                        ${cart.total?.toLocaleString() || 0}
                      </p>
                      <p className="text-xs text-gray-500">
                        Recovered {cart.recovered_at ? new Date(cart.recovered_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Schedule Email Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-600" />
              Schedule Recovery Email
            </DialogTitle>
          </DialogHeader>
          {selectedCartForSchedule && (
            <div className="space-y-4 pt-2">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">To:</span> {selectedCartForSchedule.customer_name || 'Anonymous'}
                </p>
                <p className="text-sm text-gray-500">{selectedCartForSchedule.email}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Cart Value: <span className="font-bold">${selectedCartForSchedule.total?.toLocaleString()}</span>
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Send After (hours)</label>
                <Input
                  type="number"
                  min="1"
                  max="168"
                  value={scheduleDelay}
                  onChange={(e) => setScheduleDelay(parseInt(e.target.value) || 1)}
                  data-testid="schedule-delay-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email will be sent at approximately {new Date(Date.now() + scheduleDelay * 60 * 60 * 1000).toLocaleString()}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeDiscount"
                  checked={includeDiscount}
                  onChange={(e) => setIncludeDiscount(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="includeDiscount" className="text-sm">
                  Include discount code ({sequenceSettings.discount_code})
                </label>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowScheduleModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={scheduleRecoveryEmail} className="flex-1 bg-amber-500 hover:bg-amber-600">
                  Schedule Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sequence Settings Modal */}
      <Dialog open={showSequenceSettings} onOpenChange={setShowSequenceSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-[#003456]" />
              Email Sequence Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Automated Sequences</p>
                <p className="text-sm text-gray-500">Send emails automatically based on timing</p>
              </div>
              <button
                onClick={() => setSequenceSettings({...sequenceSettings, sequence_enabled: !sequenceSettings.sequence_enabled})}
                className={`p-2 rounded-lg transition-colors ${
                  sequenceSettings.sequence_enabled ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {sequenceSettings.sequence_enabled ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
              </button>
            </div>

            {/* Email Timing */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700">Email Timing (hours after abandonment)</h4>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Email 1</label>
                  <Input
                    type="number"
                    min="1"
                    value={sequenceSettings.email_1_delay_hours}
                    onChange={(e) => setSequenceSettings({...sequenceSettings, email_1_delay_hours: parseInt(e.target.value) || 1})}
                  />
                  <div className="flex items-center gap-1 mt-1">
                    <input
                      type="checkbox"
                      id="email1Discount"
                      checked={sequenceSettings.email_1_include_discount}
                      onChange={(e) => setSequenceSettings({...sequenceSettings, email_1_include_discount: e.target.checked})}
                      className="rounded text-xs"
                    />
                    <label htmlFor="email1Discount" className="text-xs text-gray-500">+ Discount</label>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Email 2</label>
                  <Input
                    type="number"
                    min="1"
                    value={sequenceSettings.email_2_delay_hours}
                    onChange={(e) => setSequenceSettings({...sequenceSettings, email_2_delay_hours: parseInt(e.target.value) || 24})}
                  />
                  <div className="flex items-center gap-1 mt-1">
                    <input
                      type="checkbox"
                      id="email2Discount"
                      checked={sequenceSettings.email_2_include_discount}
                      onChange={(e) => setSequenceSettings({...sequenceSettings, email_2_include_discount: e.target.checked})}
                      className="rounded text-xs"
                    />
                    <label htmlFor="email2Discount" className="text-xs text-gray-500">+ Discount</label>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Email 3</label>
                  <Input
                    type="number"
                    min="1"
                    value={sequenceSettings.email_3_delay_hours}
                    onChange={(e) => setSequenceSettings({...sequenceSettings, email_3_delay_hours: parseInt(e.target.value) || 72})}
                  />
                  <div className="flex items-center gap-1 mt-1">
                    <input
                      type="checkbox"
                      id="email3Discount"
                      checked={sequenceSettings.email_3_include_discount}
                      onChange={(e) => setSequenceSettings({...sequenceSettings, email_3_include_discount: e.target.checked})}
                      className="rounded text-xs"
                    />
                    <label htmlFor="email3Discount" className="text-xs text-gray-500">+ Discount</label>
                  </div>
                </div>
              </div>
            </div>

            {/* Discount Settings */}
            <div className="space-y-3 pt-2 border-t">
              <h4 className="font-medium text-sm text-gray-700">Discount Code</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Code</label>
                  <Input
                    value={sequenceSettings.discount_code}
                    onChange={(e) => setSequenceSettings({...sequenceSettings, discount_code: e.target.value.toUpperCase()})}
                    placeholder="COMEBACK10"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Value</label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="1"
                      value={sequenceSettings.discount_value}
                      onChange={(e) => setSequenceSettings({...sequenceSettings, discount_value: parseInt(e.target.value) || 10})}
                      className="flex-1"
                    />
                    <select
                      value={sequenceSettings.discount_type}
                      onChange={(e) => setSequenceSettings({...sequenceSettings, discount_type: e.target.value})}
                      className="px-2 border rounded-lg text-sm"
                    >
                      <option value="percentage">%</option>
                      <option value="fixed">$</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowSequenceSettings(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={saveSequenceSettings} 
                disabled={savingSettings}
                className="flex-1 bg-[#003456] hover:bg-[#002040]"
              >
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersTab;
